import { useEffect, useState, useMemo, type ReactNode } from 'react';
import { parse } from 'node-html-parser';
import { BrandContext } from './BrandContext';
import API from '../services/apiService';
import cookies from '../utils/cookies';

let THEME_BASE_URL = 'https://dlthemes.amersc.com/shop/templates';

// const VITE_APP_MODE = import.meta.env.MODE;
const NODE_ENV = process.env.NODE_ENV?.toLowerCase();
// const isDevMode = import.meta.env.DEV;

if (NODE_ENV === 'staging') {
  THEME_BASE_URL = THEME_BASE_URL.replace('dlthemes', 'mlthemes');
}
if (NODE_ENV === 'production') {
  THEME_BASE_URL = THEME_BASE_URL.replace('dlthemes', 'themes');
}

export default function BrandProvider({ children }: { children: ReactNode }) {
  const queryParams = new URLSearchParams(window.location.search);
  const prevProductIds = queryParams.get('prevProductIds');

  const inAppVisit = prevProductIds || cookies.get('prevProductIds') ? true : false;

  const defaultBrand = useMemo(() => {
    // const regex = /(dlshop|mlshop|shop|dlcart|mlcart|cart|dlstore|mlstore|store)\./;
    const regex = /(dl|ml)?(cart|shop|store|catalog)(\.|-)/;
    let domain = 'certus.com';

    // this is where the dynamic brand is determined using the hostname this app is being loaded from
    if (!window.location.hostname.includes('localhost')) {
      domain = window.location.hostname.replace(regex, '');
    }

    // set the template folder to catalog.certus.com if the domain is certus.com
    domain =
      domain === 'certus.com' && inAppVisit
        ? 'catalog.certus.com'
        : domain === 'certus.com' && !inAppVisit
        ? 'certus.com'
        : domain;

    const brand = {
      domain,
      host: domain === 'catalog.certus.com' ? 'certus.com' : domain || 'certus.com',
      header: sessionStorage.getItem('header') || null,
      footer: sessionStorage.getItem('footer') || null,
      useFallback: false,
      cssFileUrl: `${THEME_BASE_URL}/${domain}/css/shop.css`,
      serverError: null as string | null,
    };
    return {
      ...brand,
    };
  }, []);

  const [brand, setBrand] = useState(defaultBrand);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTemplate = async (domain: string) => {
      try {
        const resp = await API.getTemplate(domain, controller.signal);
        const data = await resp?.data;
        const parsedHtml = parse(data);

        const header = parsedHtml.querySelector('#header-wrap');
        const footer = parsedHtml.querySelector('#footer-wrap');

        header?.querySelector('a.logo')?.setAttribute('href', '/');
        header?.querySelector('a.help-link')?.setAttribute('href', '/contact-us/');

        const fetchedBrand = {
          ...defaultBrand,
          header: header?.outerHTML ?? null,
          footer: footer?.outerHTML ?? null,
        };

        sessionStorage.setItem('header', fetchedBrand.header || '');
        sessionStorage.setItem('footer', fetchedBrand.footer || '');

        setBrand((brand) => ({
          ...brand,
          ...fetchedBrand,
        }));
      } catch (err) {
        const error = err as { name?: string; response?: { data?: any } };

        if (error?.name === 'CanceledError') {
          console.error('Request Aborted');
        } else if (error?.name === 'AxiosError') {
          // console.error(error);
          console.error(error?.response?.data);
        } else {
          console.error(error);
        }

        // when the API call fails or if there's no existing template use the default certus.com template as the fallback
        if ((error?.name ?? '') !== 'CanceledError') {
          sessionStorage.removeItem('header');
          sessionStorage.removeItem('footer');

          const fallbackBrand = {
            ...defaultBrand,
            header: null,
            footer: null,
            useFallback: true,
            cssFileUrl: `${THEME_BASE_URL}/${defaultBrand.domain}/css/shop.css`,
            serverError: error instanceof Error ? error.message : 'Unknown error',
          };

          setBrand((brand) => ({
            ...brand,
            ...fallbackBrand,
          }));
        }
      }
    };

    fetchTemplate(defaultBrand.domain);

    return () => controller.abort();
  }, [defaultBrand]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}
